/** Format ISO date for API query params (UTC). */
export function toApiDate(value: Date | string | null | undefined): string | undefined {
  if (!value) return undefined;
  if (value instanceof Date) return value.toISOString();
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value) && !value.endsWith('Z')) {
    return new Date(value).toISOString();
  }
  return value;
}

export function defaultRangeDays(days = 7): { from: Date; to: Date } {
  const to = new Date();
  const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
  return { from, to };
}

export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

export function formatNumber(n: number | null | undefined): string {
  if (n == null) return '0';
  return Number(n).toLocaleString();
}

/** bot_rate from API is 0..1 fraction */
export function formatBotRate(rate: number | null | undefined): string {
  if (rate == null) return '0%';
  const pct = Number(rate) <= 1 ? Number(rate) * 100 : Number(rate);
  return `${pct.toFixed(1)}%`;
}

export async function copyText(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const el = document.createElement('textarea');
  el.value = text;
  el.style.position = 'fixed';
  el.style.left = '-9999px';
  document.body.appendChild(el);
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
}

export function trackingUrl(domainName: string | undefined | null, code: string | undefined | null): string {
  if (!domainName || !code) return '';
  return `https://${domainName}/t/${code}`;
}

/** Same alphabet as tracker `_gen_code` (ascii letters + digits); UI default length is 3. */
const LINK_CODE_ALPHABET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

export function generateLinkCode(length = 3): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  let out = '';
  for (let i = 0; i < length; i++) {
    out += LINK_CODE_ALPHABET[bytes[i]! % LINK_CODE_ALPHABET.length];
  }
  return out;
}

export function ttlToExpiresAt(ttlHours: number | null | undefined): string | null {
  if (!ttlHours && ttlHours !== 0) return null;
  const hours = Number(ttlHours);
  if (!Number.isFinite(hours) || hours <= 0) return null;
  return new Date(Date.now() + hours * 3600 * 1000).toISOString();
}

/** base64url encode UTF-8 string (for target_url path keys) */
export function encodeTargetKey(targetUrl: string): string {
  const bytes = new TextEncoder().encode(targetUrl);
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

export function decodeTargetKey(key: string): string {
  const padded = key.replace(/-/g, '+').replace(/_/g, '/');
  const pad = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
  const bin = atob(padded + pad);
  const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}
